// Fechas de un contrato en el Cardex (P15 S07 R14, R15; plan §4): Hoy en la Ciudad de México, vigente,
// futuro, anterior y el orden de la carpeta «Contratos de trabajo».
//
// Puro: no llama a nada. Las fechas del contrato (`fechaInicio`, `fechaFin`) son días calendario
// ("2026-10-15", `date` en el OpenAPI) y se comparan como texto AAAA-MM-DD, sin hora ni zona.
//
// **Hoy es el de la Ciudad de México** (arquitectura §3.8, A25), no la fecha del navegador: un navegador
// en otra zona horaria marca el mismo contrato como vigente (E27). Se calcula con el desfase fijo UTC−06:00
// y no con `Intl.DateTimeFormat({ timeZone: "America/Mexico_City" })`, como sugiere el plan: la Ciudad de
// México no tiene horario de verano desde el 30 de octubre de 2022, y los motores con datos de zona
// anteriores (el Node 17.9.1 de este proyecto, navegadores sin actualizar) todavía le restan una hora de
// abril a octubre, así que «Hoy» y las horas del historial saldrían corridos.
// La regla de traslape es la de S02: el contrato cubre hoy si `FechaInicio <= Hoy` y (`FechaFin` nula o
// `FechaFin >= Hoy`). El texto y el color de la vigencia del renglón NO están aquí: son los del indicador
// de S02 (`Vistas/Personal/indicadorContrato.js`), que `contratos-carpeta.js` reusa con este Hoy (D11.27).

export const INDETERMINADO = "Indeterminado";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// UTC−06:00 fijo: México quitó el horario de verano en 2022 (Ciudad de México); si vuelve, cambiar aquí.
const DESFASE_CDMX_MS = -6 * 60 * 60 * 1000;
const Dos = (n) => String(n).padStart(2, "0");

// Las partes de una fecha en la Ciudad de México.
function PartesCDMX(Fecha) {
  const F = new Date(Fecha.getTime() + DESFASE_CDMX_MS);
  return {
    year: String(F.getUTCFullYear()),
    month: Dos(F.getUTCMonth() + 1),
    day: Dos(F.getUTCDate()),
    hour: Dos(F.getUTCHours()),
    minute: Dos(F.getUTCMinutes()),
  };
}

/** Hoy en la Ciudad de México, "AAAA-MM-DD". `Ahora` es para las pruebas (por omisión, el reloj). */
export function hoyCDMX(Ahora = new Date()) {
  const P = PartesCDMX(Ahora);
  return `${P.year}-${P.month}-${P.day}`;
}

/** El día calendario "AAAA-MM-DD" de una fecha del contrato, o null. */
export function dia(Valor) {
  if (Valor == null || Valor === "") return null;
  const S = String(Valor);
  return /^\d{4}-\d{2}-\d{2}/.test(S) ? S.slice(0, 10) : null;
}

/** El contrato cubre `Hoy` (R15): empezó y no ha terminado. Uno futuro no es vigente. */
export function esVigente(Contrato, Hoy) {
  const Ini = dia(Contrato && Contrato.fechaInicio);
  if (!Ini || !Hoy) return false;
  const Fin = dia(Contrato.fechaFin);
  return Ini <= Hoy && (Fin == null || Fin >= Hoy);
}

/** Todavía no empieza. */
export function esFuturo(Contrato, Hoy) {
  const Ini = dia(Contrato && Contrato.fechaInicio);
  return !!Ini && !!Hoy && Ini > Hoy;
}

/** Ya terminó: su `FechaFin` es anterior a hoy. */
export function esAnterior(Contrato, Hoy) {
  const Fin = dia(Contrato && Contrato.fechaFin);
  return !!Fin && !!Hoy && Fin < Hoy;
}

/**
 * El orden de la carpeta (R14): del más reciente al más antiguo, mayor `FechaInicio` primero; con la misma
 * fecha, el mayor consecutivo. No cambia la lista que recibe.
 */
export function ordenaPorReciente(Contratos) {
  return (Contratos || []).slice().sort((a, b) => {
    const A = dia(a && a.fechaInicio) || "";
    const B = dia(b && b.fechaInicio) || "";
    if (A !== B) return A < B ? 1 : -1;
    return ((b && b.consecutivo) || 0) - ((a && a.consecutivo) || 0);
  });
}

/** «15/oct/2026», o «Indeterminado» para un fin nulo (R14). */
export function fechaLarga(Valor, Nulo = INDETERMINADO) {
  const D = dia(Valor);
  if (!D) return Nulo;
  const [A, M, Di] = D.split("-");
  return `${Di}/${MESES[Number(M) - 1]}/${A}`;
}

/** «15/oct/2026 10:20» de una fecha y hora (`date-time`), en la Ciudad de México (S08 R21, R33). */
export function fechaHoraCDMX(Valor) {
  if (Valor == null || Valor === "") return "";
  const F = new Date(Valor);
  if (isNaN(F.getTime())) return "";
  const P = PartesCDMX(F);
  return `${P.day}/${MESES[Number(P.month) - 1]}/${P.year} ${P.hour}:${P.minute}`;
}

export default { INDETERMINADO, hoyCDMX, dia, esVigente, esFuturo, esAnterior, ordenaPorReciente, fechaLarga, fechaHoraCDMX };
